import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Refrigerator, Snowflake } from "lucide-react";
import { PosizioneFrigoSelect } from "./PosizioneFrigoSelect";

/**
 * Dialog HACCP per scongelare un lotto congelato.
 * - Da ABBATTITORE: scelta destinazione Frigo (scongelato) o Congelatore (resta congelato).
 * - Da CONGELATORE: va sempre in Frigo (scongelato).
 * - Scongelamento parziale (split): quantità < totale → nuovo lotto in frigo, originale ridotto.
 */
const ScongelaLottoDialog = ({
  lotto,
  open,
  onOpenChange,
  form,
  setForm,
  saving,
  onConferma,
  getNomeIngrediente,
  frigoriferi = [], posizioni = [],
}) => {
  const isAbbattitore = (lotto?.metodo_congelamento || "").toLowerCase() === "abbattitore";
  const destinazione = form.destinazione || "frigo";
  const inCongelatore = destinazione === "congelatore";
  const qTot = lotto?.quantita_preparata;
  const um = lotto?.unita_misura || "kg";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="text-xl">☀️</span>
            {isAbbattitore ? "Sposta / Scongela lotto" : "Scongela lotto"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-muted-foreground">
            <strong>{lotto && getNomeIngrediente(lotto.ingrediente_id, lotto.prodotto_id, lotto)}</strong> — Lotto {lotto?.numero_lotto || "-"}
            {qTot != null && qTot !== "" && (
              <> · Quantità: <strong>{qTot} {um}</strong></>
            )}
            {lotto?.metodo_congelamento && (
              <><br />Ambiente attuale: <strong>{isAbbattitore ? "Abbattitore" : "Congelatore"}</strong></>
            )}
          </p>

          {/* Scelta destinazione (solo da abbattitore) */}
          {isAbbattitore && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Destinazione</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, destinazione: "frigo" }))}
                  className={`flex items-center gap-2 rounded-xl border p-3 text-sm transition-colors ${destinazione === "frigo" ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400" : "border-border hover:bg-accent"}`}
                  data-testid="scongela-dest-frigo"
                >
                  <Refrigerator className="h-4 w-4" /> Frigo (scongela)
                </button>
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, destinazione: "congelatore" }))}
                  className={`flex items-center gap-2 rounded-xl border p-3 text-sm transition-colors ${destinazione === "congelatore" ? "border-sky-500 bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400" : "border-border hover:bg-accent"}`}
                  data-testid="scongela-dest-congelatore"
                >
                  <Snowflake className="h-4 w-4" /> Congelatore
                </button>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Data {inCongelatore ? "spostamento" : "scongelamento"}</Label>
            <Input
              type="date"
              value={form.data}
              onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))}
              data-testid="scongela-data"
            />
          </div>
          <PosizioneFrigoSelect value={qTot != null && parseFloat(form.quantita_parziale) > 0 && parseFloat(form.quantita_parziale) < Number(qTot)
            ? (form.posizione ?? "") : (form.posizione ?? lotto?.posizione ?? "")}
            onChange={v => setForm(p => ({ ...p, posizione: v }))}
            frigoriferi={frigoriferi} posizioniLegacy={posizioni} testId="scongela-posizione" />
          {qTot != null && parseFloat(form.quantita_parziale) > 0 && parseFloat(form.quantita_parziale) < Number(qTot) && (
            <p className="text-xs text-muted-foreground" data-testid="scongela-posizione-figlio-avviso">Nello split la posizione riguarda solo il lotto figlio.</p>
          )}

          {inCongelatore ? (
            <>
              {/* Spostamento parziale in congelatore */}
              {qTot != null && qTot !== "" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Quantità da spostare in congelatore (opzionale)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.001"
                      max={qTot}
                      value={form.quantita_parziale}
                      onChange={(e) => setForm((p) => ({ ...p, quantita_parziale: e.target.value }))}
                      placeholder={`Tutto (${qTot} ${um})`}
                      className="w-40"
                      data-testid="scongela-quantita-parziale"
                    />
                    <span className="text-sm text-muted-foreground">{um} su {qTot}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Lasciando vuoto sposti tutto in congelatore. Inserendo meno del totale, crei un nuovo lotto in congelatore e l'originale resta in abbattitore con la quantità rimanente.
                  </p>
                </div>
              )}
              <div className="bg-sky-50 dark:bg-sky-500/10 rounded-xl p-3 text-xs text-sky-700 dark:text-sky-400">
                ❄️ Il lotto resta <strong>congelato</strong> e viene spostato dall'abbattitore al congelatore. La scadenza da congelato non cambia.
              </div>
            </>
          ) : (
            <>
              {/* Scongelamento parziale */}
              {qTot != null && qTot !== "" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Quantità da scongelare (opzionale)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.001"
                      max={qTot}
                      value={form.quantita_parziale}
                      onChange={(e) => setForm((p) => ({ ...p, quantita_parziale: e.target.value }))}
                      placeholder={`Tutto (${qTot} ${um})`}
                      className="w-40"
                      data-testid="scongela-quantita-parziale"
                    />
                    <span className="text-sm text-muted-foreground">{um} su {qTot}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Lasciando vuoto scongeli tutto. Inserendo meno del totale, crei un nuovo lotto scongelato in frigo e l'originale resta congelato con la quantità rimanente.
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Validità dopo scongelamento</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="14"
                    value={form.giorni}
                    onChange={(e) => setForm((p) => ({ ...p, giorni: e.target.value, data_scadenza_custom: "" }))}
                    className="w-24"
                    data-testid="scongela-giorni"
                  />
                  <span className="text-sm text-muted-foreground">giorni</span>
                  <span className="text-xs text-muted-foreground ml-auto">oppure data specifica ↓</span>
                </div>
                <Input
                  type="date"
                  value={form.data_scadenza_custom}
                  onChange={(e) => setForm((p) => ({ ...p, data_scadenza_custom: e.target.value }))}
                  className="text-sm"
                  placeholder="Data scadenza personalizzata"
                  data-testid="scongela-data-custom"
                />
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Note (opzionale)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              placeholder="es. da consumare entro 48h"
              data-testid="scongela-note"
            />
          </div>

          {!inCongelatore && (
            <div className="bg-orange-50 dark:bg-orange-500/10 rounded-xl p-3 text-xs text-orange-700 dark:text-orange-400">
              ⚠️ Una volta scongelato, il prodotto NON deve essere ri-congelato per legge.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            onClick={onConferma}
            disabled={saving}
            className={inCongelatore ? "bg-sky-600 hover:bg-sky-700 text-white" : "bg-orange-600 hover:bg-orange-700 text-white"}
            data-testid="scongela-conferma"
          >
            {saving ? "..." : inCongelatore ? "❄️ Sposta in congelatore" : "☀️ Scongela"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScongelaLottoDialog;
